import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { artisanProfileService, type CreateArtisanProfileRequest } from '../../../services/artisanProfileService';
import { useLanguage } from '../../../features/language/useLanguage';
import { readArtisanFile, type ArtisanFileErrorCode } from '../utils/readArtisanFile';
import type { ParsedArtisanData } from '../utils/artisanParser';
import type { RoastProfileRow } from '../../roasts/component/types';
import { getErrorMessage, getErrorStatus, getDuplicateProfileId } from './useArtisanSyncPage';

/** 'alreadyStaged' is decided here rather than in the reader: it is about the batch, not the file. */
export type StagedErrorCode = ArtisanFileErrorCode | 'alreadyStaged';

export type StagedStatus = 'ready' | 'invalid' | 'saving' | 'saved' | 'duplicate' | 'failed';

export interface StagedRoast {
    id: string;
    file: File;
    fileName: string;
    status: StagedStatus;
    /** Null only when the file could not be read. */
    parsed: ParsedArtisanData | null;
    identity: string;
    /** Hand edits made in the preview. Null means "exactly as imported". */
    edited: RoastProfileRow[] | null;
    errorCode?: StagedErrorCode;
    errorDetail?: string;
    /** Set once the roast is in the database, or when it was already there. */
    profileId?: string;
    /** False when the roast saved but its original file could not be stored. */
    fileStored?: boolean;
    failure?: string;
}

// A counter rather than crypto.randomUUID, which needs a secure context the dev server
// over plain HTTP does not always provide.
let sequence = 0;
const nextId = () => `staged-${++sequence}`;

export function useArtisanStaging() {
    const { t } = useLanguage();
    const queryClient = useQueryClient();

    const [items, setItems] = useState<StagedRoast[]>([]);
    const [activeId, setActiveId] = useState<string | null>(null);
    const [readProgress, setReadProgress] = useState<{ done: number; total: number } | null>(null);
    const [saveProgress, setSaveProgress] = useState<{ done: number; total: number } | null>(null);
    const [hasSaved, setHasSaved] = useState(false);

    /**
     * Reads files one at a time and appends them to the batch. Sequential on purpose:
     * a dozen multi-megabyte exports parsed at once locks the main thread, and the
     * progress count is only honest if the work is ordered.
     */
    const addFiles = useCallback(async (files: File[]) => {
        if (files.length === 0) return;
        setReadProgress({ done: 0, total: files.length });

        const staged: StagedRoast[] = [];
        const seen = new Set(items.map(i => i.identity).filter(Boolean));

        for (const file of files) {
            const result = await readArtisanFile(file);
            if (!result.ok) {
                staged.push({
                    id: nextId(), file, fileName: file.name, status: 'invalid',
                    parsed: null, identity: '', edited: null,
                    errorCode: result.error.code, errorDetail: result.error.detail,
                });
            } else if (seen.has(result.content.identity)) {
                // Caught here so the roaster sees it on the tab instead of after a
                // round-trip that was always going to come back 409.
                staged.push({
                    id: nextId(), file, fileName: file.name, status: 'invalid',
                    parsed: result.content.parsed, identity: result.content.identity, edited: null,
                    errorCode: 'alreadyStaged',
                });
            } else {
                seen.add(result.content.identity);
                staged.push({
                    id: nextId(), file, fileName: file.name, status: 'ready',
                    parsed: result.content.parsed, identity: result.content.identity, edited: null,
                });
            }
            setReadProgress(prev => (prev ? { ...prev, done: prev.done + 1 } : prev));
        }

        setItems(prev => [...prev, ...staged]);
        // Land on the first file of the batch that is worth looking at, falling back to
        // its first file so an all-invalid drop still shows its reason.
        setActiveId(prev => prev ?? staged.find(s => s.status === 'ready')?.id ?? staged[0]?.id ?? null);
        setReadProgress(null);
    }, [items]);

    const removeItem = useCallback((id: string) => {
        setItems(prev => {
            const next = prev.filter(i => i.id !== id);
            setActiveId(current => {
                if (current !== id) return current;
                const removedAt = prev.findIndex(i => i.id === id);
                // Step to the neighbour that took its place, so removing a tab keeps the
                // reading position rather than jumping back to the start.
                return next[Math.min(removedAt, next.length - 1)]?.id ?? null;
            });
            return next;
        });
    }, []);

    const setEdited = useCallback((id: string, rows: RoastProfileRow[]) => {
        setItems(prev => prev.map(i => (i.id === id ? { ...i, edited: rows } : i)));
    }, []);

    const reset = useCallback(() => {
        setItems([]);
        setActiveId(null);
        setReadProgress(null);
        setSaveProgress(null);
        setHasSaved(false);
    }, []);

    const buildPayload = useCallback((item: StagedRoast): CreateArtisanProfileRequest => {
        const parsed = item.parsed!;
        const stageSummary: Record<string, { time: string; temp: number | string }> = {};
        parsed.stages.forEach(s => {
            stageSummary[s.key] = { time: s.time, temp: s.temp };
        });

        return {
            title: parsed.metadata.title || item.fileName,
            roaster: parsed.metadata.operator || t('artisanSync.values.unknown'),
            machine: parsed.metadata.machine || t('artisanSync.values.unknown'),
            roast_date: parsed.metadata.roastDate || new Date().toISOString().split('T')[0],
            roast_time: parsed.metadata.roastTime || '00:00:00',
            drum_speed: parsed.metadata.drumSpeed || '',
            roast_uuid: item.identity,
            recipe: {
                // Left as the parser found it (empty). 'Light' was never a value the
                // Roast Type selection offers, and the work order owns this field anyway.
                roast_type: parsed.roastType,
                input_weight: parsed.metadata.weightIn,
                output_weight: parsed.metadata.weightOut,
                agtron: { wholeBean: parsed.metadata.wholeColor, groundBean: parsed.metadata.groundColor },
                profile: item.edited ?? parsed.profile,
                stage_summary: stageSummary,
            },
        };
    }, [t]);

    /**
     * Saves every readable roast in order, one request each, so the reviewed profile
     * travels with its own file. A file that fails does not stop the ones behind it —
     * each roast is a separate record, and one bad export must not discard a day's work.
     *
     * Returns the finished batch so the caller can act on the outcome without waiting
     * for the next render.
     */
    const saveAll = useCallback(async (): Promise<StagedRoast[]> => {
        const working = items.map(i => ({ ...i }));
        const queue = working.filter(i => i.status === 'ready');
        if (queue.length === 0) return working;

        setSaveProgress({ done: 0, total: queue.length });

        for (const item of queue) {
            item.status = 'saving';
            setItems(working.map(i => ({ ...i })));

            try {
                const profile = await artisanProfileService.uploadWithFile(buildPayload(item), item.file);
                item.status = 'saved';
                item.profileId = profile.id;
                item.fileStored = profile.has_raw_file;
            } catch (error: unknown) {
                if (getErrorStatus(error) === 409) {
                    item.status = 'duplicate';
                    item.profileId = getDuplicateProfileId(error);
                } else {
                    item.status = 'failed';
                    item.failure = getErrorMessage(error, t('artisanSync.errors.saveProfile'));
                }
            }

            setItems(working.map(i => ({ ...i })));
            setSaveProgress(prev => (prev ? { ...prev, done: prev.done + 1 } : prev));
        }

        setSaveProgress(null);
        setHasSaved(true);
        queryClient.invalidateQueries({ queryKey: ['artisan-profiles'] });
        return working;
    }, [items, buildPayload, queryClient, t]);

    return {
        items,
        activeId,
        setActiveId,
        addFiles,
        removeItem,
        setEdited,
        reset,
        saveAll,
        readProgress,
        saveProgress,
        hasSaved,
        readyCount: items.filter(i => i.status === 'ready').length,
    };
}
